// Builds the media-list directory (dashboard#media_lists) from `pageData`. The markup it clones
// lives in `#media-item-template` in app/views/dashboard/media_lists.html.erb; the styling of every
// class it writes lives in mack/sass/components/common/_media-list.scss.
//
// Shape: docs/design-system/ui_kits/customer_app/MediaListsScreen.jsx. The region picker is a row
// of individual pills, opening on "All regions" — the whole directory at once, each region under
// its own section header (name left, category count right) — and narrowing to a single section
// when one region's pill is pressed. Every region is rendered into the DOM here (the whole payload
// is already on the page — a filter needs no round trip) and the sections a pill excludes are
// hidden rather than rebuilt.

// Categories can be stored without an icon, and the design system never shows an empty tinted tile
// (docs/design-system/readme.md, "Iconography") — this is what an icon-less category falls back to.
const DEFAULT_ITEM_ICON = 'mdi mdi-newspaper-variant-outline';

// The pill's pressed paint. `segmented-option-active` is kept alongside it: it is the app's shared
// active-key class (mack/sass/components/common/_form.scss) and this file has always written it.
const ACTIVE_REGION_CLASS = 'segmented-option-active';
const ACTIVE_PILL_CLASS = 'region-pill-active';

// Set on every region section the pressed pill excludes.
const HIDDEN_REGION_CLASS = 'region-group-inactive';

// The first pill's target: every section stays on screen.
const ALL_REGIONS_TARGET = 'all';

export class MediaList {
    constructor(data, labels = {}) {
        this.data = data;
        this.labels = labels || {};
        this.init();
    }

    init() {
        this.renderRegionButtons();
        this.setupEventListeners();
    }

    renderRegionButtons() {
        const regions = this.groupByRegion();
        const regionButtonsContainer = document.querySelector('.region-buttons-container');
        const regionContentContainer = document.querySelector('.region-content-container');
        // Turbolinks restores the cached DOM — including everything rendered below — before firing
        // `turbolinks:load`, so both containers are emptied or a revisit would double every region.
        regionButtonsContainer.innerHTML = '';
        regionContentContainer.innerHTML = '';

        const pillRow = document.createElement('div');
        pillRow.className = 'media-lists-regions region-pills';
        pillRow.setAttribute('role', 'group');
        if (this.labels.region_selector) {
            pillRow.setAttribute('aria-label', this.labels.region_selector);
        }
        regionButtonsContainer.appendChild(pillRow);

        pillRow.appendChild(this.buildRegionButton(this.allRegionsLabel(), ALL_REGIONS_TARGET));

        Object.keys(regions).forEach((region, index) => {
            pillRow.appendChild(this.buildRegionButton(region, `region-content-${index}`));
            this.renderRegionContent(index, regions[region]);
        });

        this.selectRegion(pillRow.querySelector('.region-button'));
    }

    // `region-button` is the hook this file has always selected on; `region-pill` is what gives the
    // key its own outlined shape, and `segmented-option` stays so the app's one active-key class
    // still lands on something it styles.
    buildRegionButton(label, target) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.className = 'segmented-option region-button region-pill';
        button.dataset.target = target;
        button.setAttribute('aria-pressed', 'false');
        return button;
    }

    renderRegionContent(index, items) {
        const regionContentContainer = document.querySelector('.region-content-container');
        const regionDiv = document.createElement('div');
        regionDiv.className = 'region-group';
        regionDiv.id = `region-content-${index}`;

        // "All regions" puts every section on screen at once, so each one states its own name and
        // how many categories it holds — the header the kit gives a region above its grid.
        const header = document.createElement('div');
        header.className = 'region-group-header';

        const heading = document.createElement('h3');
        heading.className = 'card-heading no-gutter region-group-heading';
        heading.id = `region-heading-${index}`;
        heading.textContent = items[0].region_name;
        header.appendChild(heading);

        const meta = document.createElement('span');
        meta.className = 'region-group-meta';
        meta.textContent = this.regionMetaLabel(items.length);
        header.appendChild(meta);

        regionDiv.setAttribute('role', 'group');
        regionDiv.setAttribute('aria-labelledby', heading.id);
        regionDiv.appendChild(header);

        const itemBody = document.createElement('div');
        itemBody.className = 'media-items-wrapper';
        const rowDiv = document.createElement('div');
        rowDiv.className = 'row';

        const template = document.getElementById('media-item-template').content;
        items.forEach(item => {
            const clone = document.importNode(template, true);
            clone.querySelector('.icon-placeholder').className += ` ${item.icon || DEFAULT_ITEM_ICON}`;
            clone.querySelector('.name-placeholder').textContent = item.name;
            clone.querySelector('.media-item-description').textContent = item.description;

            const downloadLink = clone.querySelector('.download-link');
            downloadLink.href = item.attachment_url;
            // The button reads "PDF" — a file format, the same three letters in either locale —
            // because at this size the label sits beside the copy rather than under it. The
            // translated sentence stays as the link's accessible name.
            if (this.labels.attachment_url) {
                downloadLink.setAttribute('aria-label', `${item.name} — ${this.labels.attachment_url}`);
            }

            rowDiv.appendChild(clone);
        });

        itemBody.appendChild(rowDiv);
        regionDiv.appendChild(itemBody);
        regionContentContainer.appendChild(regionDiv);
    }

    allRegionsLabel() {
        return this.labels.region_fallback || 'All regions';
    }

    // `region_meta` is a pluralised key, so both forms arrive with their `%{count}` placeholder
    // intact and the number is substituted here, where the count is known.
    regionMetaLabel(count) {
        const template = count === 1 ? this.labels.region_meta_one : this.labels.region_meta_other;
        return template ? template.replace('%{count}', String(count)) : String(count);
    }

    // Exclusive: one pill is pressed, and it decides which sections are on screen — every one of
    // them under "All regions", otherwise just the one it names.
    selectRegion(button) {
        if (!button) return;

        const target = button.dataset.target;

        document.querySelectorAll('.region-buttons-container .region-button').forEach(option => {
            const active = option === button;
            option.classList.toggle(ACTIVE_REGION_CLASS, active);
            option.classList.toggle(ACTIVE_PILL_CLASS, active);
            option.setAttribute('aria-pressed', String(active));
        });

        document.querySelectorAll('.region-content-container .region-group').forEach(group => {
            const hidden = target !== ALL_REGIONS_TARGET && group.id !== target;
            group.classList.toggle(HIDDEN_REGION_CLASS, hidden);
        });
    }

    setupEventListeners() {
        document.querySelectorAll('.region-buttons-container .region-button').forEach(button => {
            button.addEventListener('click', () => this.selectRegion(button));
        });
    }

    groupByRegion() {
        return this.data.reduce((acc, item) => {
            const regionName = item.region_name;
            if (!acc[regionName]) {
                acc[regionName] = [];
            }
            acc[regionName].push(item);
            return acc;
        }, {});
    }
}

document.addEventListener('turbolinks:load', () => {
    // `pageData` is declared (via inline `var pageData = …`) only on pages that
    // need it (dashboard, release form). On other pages — e.g. sign-up — the
    // bare reference would throw a ReferenceError, so guard with typeof first
    // (optional chaining alone does NOT guard an undeclared variable).
    if (typeof pageData !== 'undefined' && pageData?.mediaLists) {
        new MediaList(pageData.mediaLists, pageData.columnTitles);
    }
});

// Which pill is pressed is transient state, so it is not what the back button should restore.
// Emptying both containers before the snapshot is taken also means a revisit never has generated
// markup to trip over — it rebuilds from `pageData` on `turbolinks:load`.
document.addEventListener('turbolinks:before-cache', () => {
    document.querySelectorAll('.region-buttons-container, .region-content-container').forEach(container => {
        container.innerHTML = '';
    });
});
